import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import ReactGA from 'react-ga4';
import { optionalTelemetryAllowed } from '../services/appEnvironment';
import { api } from '../services/api';
import { auth } from '../services/firebase';
import { aiPermissionScope, hasAiPermission } from '../services/aiPermission';
import { clearPlanningDraft, reconcilePlanningDraftOwner } from '../services/planningDraft';

const emptyConversation = () => ({
  conversationHistory: [], suggestionsData: null, itineraryData: null,
  itinerary: null, isLoading: false, isRegenerating: false, error: null,
  notice: null, viewMode: 'results', revealedDayIndex: 0,
  activePlanningController: null,
});

const trackLegacyEvent = (event) => {
  if (optionalTelemetryAllowed()) ReactGA.event(event);
};

// Persistence is optional. Storage restrictions must not interrupt sign-out.
const tripStorage = {
  getItem: (key) => { try { return window.localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { window.localStorage.setItem(key, value); } catch {} },
  removeItem: (key) => { try { window.localStorage.removeItem(key); } catch {} },
};

export const useTripStore = create(
  persist(
    (set, get) => {
      const feedbackContext = () => ({
        epoch: get().conversationEpoch,
        userId: get().userId,
        itineraryId: get().itinerary?.id,
        scope: aiPermissionScope(auth.currentUser),
      });
      const isCurrentFeedback = (context) => {
        const current = feedbackContext();
        return Object.keys(current).every(key => current[key] === context[key]);
      };
      const requireFeedbackPermission = (context, resume) => {
        if (!isCurrentFeedback(context)) return false;
        if (hasAiPermission(context.scope)) return true;
        set({ notice: {
          type: 'ai-permission',
          scope: context.scope,
          onConfirm: () => {
            if (isCurrentFeedback(context) && hasAiPermission(context.scope)) return resume();
            return false;
          },
        } });
        return false;
      };

      const handleFeedback = async (feedback, isSwap, context) => {
        // A swap confirmation may outlive consent, the account or this trip.
        // Recheck immediately before starting any regeneration work.
        if (get().isLoading || !requireFeedbackPermission(context,
          () => handleFeedback(feedback, isSwap, context))) return false;
        const { itinerary, addMessage, updateMessage, setItinerary } = get();
        const { conversationEpoch, userId } = get();
        const isCurrentConversation = () => get().conversationEpoch === conversationEpoch && get().userId === userId;
        if (isSwap) {
          trackLegacyEvent({
            category: 'Itinerary',
            action: 'Swap Triggered',
            label: itinerary.id,
          });
        } else {
          trackLegacyEvent({
            category: 'Itinerary',
            action: 'Feedback Submitted',
            label: itinerary.id,
          });
        }

        set({ isLoading: true, isRegenerating: true, error: null });

        const payload = typeof feedback === 'string'
          ? { free_text: feedback }
          : {
              day: feedback.dayIndex,
              segment: feedback.segment,
              venue_id: feedback.venue_id,
              reason: feedback.reason,
            };

        const placeholderId = addMessage({
          role: 'assistant',
          content: 'On it! I am updating your itinerary based on your feedback...',
          thinking: true,
        });

        try {

          const response = await api.post(`/v1/itinerary/${itinerary.id}/feedback`, {
            ...payload,
            itinerary_id: itinerary.id,
          });

          const result = response.data;

          // Clearing/deleting an account must not be undone by an older reply.
          if (!isCurrentConversation()) return;
          if (!isCurrentFeedback(context)) {
            set({ isLoading: false, isRegenerating: false });
            updateMessage(placeholderId, { content: 'The earlier trip update finished. Reopen that trip to check its details.', thinking: false });
            return;
          }

          get().setSearchCreditsRemaining(result.search_credits_remaining ?? null);
          if (result.type === 'clarification') {
            const question = result.data?.question || result.message || 'Please add more details to your feedback.';
            set({ isLoading: false, isRegenerating: false, error: null,
              notice: { type: 'info', title: 'More details needed', description: `${question} Your current itinerary is unchanged. Submit your feedback again with these details.` } });
            updateMessage(placeholderId, { content: question, thinking: false });
            return false;
          }
          const { search_credits_remaining, quota_charged, quota_type, ...regeneratedItinerary } = result;

          setItinerary(regeneratedItinerary);
          set({ isLoading: false, isRegenerating: false, error: null, notice: null });

          updateMessage(placeholderId, {
            content: 'I’ve updated your itinerary. Let me know what you think!',
            thinking: false,
          });
          return true;

        } catch (error) {
          if (!isCurrentConversation()) return;
          const detail = error.response?.data?.detail;
          const errorMessage = (
            typeof detail === 'string' ? detail : detail?.message
          ) || 'Failed to submit feedback.';
          set({ error: errorMessage, isLoading: false, isRegenerating: false });
          updateMessage(placeholderId, {
            content: `Sorry, I ran into an issue while updating your plan: ${errorMessage}`,
            thinking: false,
          });
        }
      };

      return {
      // STATE
      conversationHistory: [],
      suggestionsData: null,
      itineraryData: null,
      itinerary: null, // New dedicated state for the itinerary payload
      isLoading: false,
      isRegenerating: false,
      error: null,
      notice: null,
      plan: 'free',
      searchCreditsRemaining: null,
      quotaRevision: 0,
      viewMode: 'results', // 'results' or 'itinerary'
      userId: null,
      conversationOwnerId: null,
      privacyEpoch: 0,
      revealedDayIndex: 0,
      conversationEpoch: 0,
      activePlanningController: null,

      // ACTIONS
      setLoading: (loading) => set({ isLoading: loading }),
      setViewMode: (mode) => set({ viewMode: mode }),
      setError: (error) => set({ error }),
      setNotice: (notice) => set({ notice }),
      setSuggestionsData: (data) => set({ suggestionsData: data }),
      setItineraryData: (data) => set({ itineraryData: data }),
      setItinerary: (itinerary) => {
        if (itinerary) {
          trackLegacyEvent({
            category: 'Itinerary',
            action: 'Itinerary Created',
            label: itinerary.id,
          });
          set({ itinerary, revealedDayIndex: 0, viewMode: 'itinerary' });
          return;
        }
        set({ itinerary, revealedDayIndex: 0 });
      },
      mergeItineraryVenueImages: (images) => set((state) => {
        if (!state.itinerary || !images || typeof images !== 'object') {
          return state;
        }
        const venues = state.itinerary.venues || {};
        const mergedVenues = Object.fromEntries(
          Object.entries(venues).map(([venueId, venue]) => [
            venueId,
            { ...venue, ...(images[venueId] || {}) },
          ]),
        );
        return {
          itinerary: { ...state.itinerary, venues: mergedVenues },
        };
      }),
      setPlan: (plan) => set({ plan }),
      revealNextDay: () => {
        trackLegacyEvent({
          category: 'Itinerary',
          action: 'Day Revealed',
        });
        set((state) => ({ revealedDayIndex: state.revealedDayIndex + 1 }));
      },
      setSearchCreditsRemaining: (value) => set((state) => ({
        searchCreditsRemaining: value,
        quotaRevision: state.quotaRevision + 1,
      })),
      setUserId: (userId) => {
        const nextUserId = typeof userId === 'string' && userId ? userId : null;
        const state = get();
        const discardedPrivateDraft = reconcilePlanningDraftOwner(nextUserId);
        const leavingAccount = state.conversationOwnerId !== null
          && state.conversationOwnerId !== nextUserId;
        if (leavingAccount || discardedPrivateDraft) {
          state.activePlanningController?.abort();
          clearPlanningDraft();
          set({
            ...emptyConversation(),
            userId: nextUserId,
            conversationOwnerId: nextUserId,
            conversationEpoch: state.conversationEpoch + 1,
            privacyEpoch: state.privacyEpoch + 1,
          });
          return;
        }
        // A guest may sign in to save the plan they just made. Same-account
        // restoration/refresh also retains the conversation, not its quota.
        set({ userId: nextUserId, conversationOwnerId: nextUserId });
      },
      beginPlanningRequest: (controller) => {
        const activeController = get().activePlanningController;
        if (activeController && activeController !== controller) {
          activeController.abort();
        }
        set({ activePlanningController: controller });
      },
      finishPlanningRequest: (controller) => set((state) => (
        state.activePlanningController === controller
          ? { activePlanningController: null }
          : state
      )),

      addMessages: (messages) => {
        const messagesWithIds = messages.map((message) => ({
          ...message,
          id: message.id || crypto.randomUUID(),
        }));

        set((state) => ({
          conversationHistory: [...state.conversationHistory, ...messagesWithIds],
        }));

        return messagesWithIds.map((msg) => msg.id);
      },

      addMessage: (message) => {
        const [id] = get().addMessages([message]);
        return id;
      },

      updateMessage: (id, newMessage) =>
        set((state) => ({
          conversationHistory: state.conversationHistory.map((msg) =>
            msg.id === id ? { ...msg, ...newMessage } : msg
          ),
        })),

      clearConversation: () => {
        get().activePlanningController?.abort();
        set((state) => ({
          ...emptyConversation(),
          conversationEpoch: state.conversationEpoch + 1,
        }));
        // Clear is an explicit context boundary. Removing the persisted copy
        // prevents old messages from reappearing after a reload.
        tripStorage.removeItem('trip-storage');
      },

      submitFeedback: async (feedback) => {
        const { itinerary } = get();
        if (!itinerary?.id) {
          set({ error: 'Cannot submit feedback: Itinerary ID is missing.' });
          return;
        }
        if (get().isLoading) return false;
        const context = feedbackContext();
        if (!requireFeedbackPermission(context, () => get().submitFeedback(feedback))) return false;

        const isSwap = typeof feedback !== 'string' && feedback.reason === 'swap';

        if (isSwap) {
          set({
            notice: {
              type: 'confirm',
              title: 'Confirm Swap',
              description: "Are you sure you want to swap this item? I'll regenerate the itinerary with a new suggestion.",
              onConfirm: async () => {
                if (!isCurrentFeedback(context)) return false;
                set({ notice: null });
                return handleFeedback(feedback, isSwap, context);
              },
              onCancel: () => set({ notice: null }),
            },
          });
          return;
        }

        return handleFeedback(feedback, isSwap, context);
      },
    }
  },
    {
      name: 'trip-storage', // key used in localStorage
      storage: createJSONStorage(() => tripStorage),
      version: 5,
      migrate: () => {
        // Legacy caches have no trustworthy owner. Do not attribute them to
        // whichever account happens to sign in next. Server-saved trips stay.
        clearPlanningDraft();
        return { ...emptyConversation(), conversationOwnerId: null };
      },
      merge: (persistedState, currentState) => {
        const owner = persistedState?.conversationOwnerId;
        if (owner !== null && (typeof owner !== 'string' || !owner)) return currentState;
        // Only restore data, never actions, authentication, quota or epochs.
        return {
          ...currentState,
          conversationOwnerId: owner,
          ...Object.fromEntries([
            'conversationHistory', 'suggestionsData', 'itineraryData',
            'itinerary', 'viewMode', 'revealedDayIndex',
          ].filter((key) => Object.prototype.hasOwnProperty.call(persistedState, key))
            .map((key) => [key, persistedState[key]])),
        };
      },
      partialize: (state) => ({
        conversationOwnerId: state.conversationOwnerId,
        conversationHistory: state.conversationHistory,
        suggestionsData: state.suggestionsData,
        itineraryData: state.itineraryData,
        itinerary: state.itinerary,
        viewMode: state.viewMode,
        revealedDayIndex: state.revealedDayIndex,
      }),
    }
  )
);
